import React from 'react';
import { List } from 'immutable';
import { twMerge } from 'tailwind-merge';

import ExtensionContactAvatar from 'flow/components/ui-components/ExtensionContactAvatar';
import MessageContent from 'flow/components/message/PbxMessageContent';
import Spacer from 'flow/components/custom-components/spacer';
import MessageItemSent from 'flow/components/message/MessageItemSent';
import useAppContext from 'flow/hooks/useAppContext';

import { extractName } from 'flow/utils';
import { messageIsFromExtension } from 'flow/utils/message-utils';

import { WidgetMessage } from 'flow/types/Channel';
import { ChatSessionKey, ExtensionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';

interface MessageWrapperProps {
    chatMessageList: List<TypedMap<WidgetMessage>>;
    sessionKey: ChatSessionKey;
}

const MessageWrapper = ({
    chatMessageList,
    sessionKey,
}: MessageWrapperProps) => {
    return (
        <div>
            {chatMessageList.reduce<React.ReactNode[]>(
                (acc, message, index) => {
                    if (index > 0) {
                        acc.push(
                            <Spacer
                                key={`message-spacer-${message.get('key')}`}
                                height={18}
                            />,
                        );
                    }
                    acc.push(
                        <div key={`message-wrapper-${message.get('key')}`}>
                            <MessageContent
                                message={message}
                                sessionKey={sessionKey}
                            />
                        </div>,
                    );
                    return acc;
                },
                [],
            )}
        </div>
    );
};

const PureMessageWrapper = React.memo(MessageWrapper);

interface MessageHeaderProps {
    message: TypedMap<WidgetMessage>;
    extensionKey: ExtensionKey;
}

const MessageHeader = ({ message, extensionKey }: MessageHeaderProps) => {
    const { i18next } = useAppContext();

    const time = message.get('sent');
    const senderIsMe = messageIsFromExtension(message, extensionKey);

    const getSenderName = (message: TypedMap<WidgetMessage>) => {
        if (!message.has('from')) {
            return i18next.t('pbx.visitor');
        }
        const from = message.getIn(['from', 'contact']);

        if (senderIsMe) {
            return `${i18next.t('chat.you')}: `;
        }

        return extractName(from, 'Visitor');
    };

    return (
        <div className="mb-2 flex gap-2 text-xs">
            <div
                className={twMerge(
                    'text-gray-700',
                    senderIsMe && 'text-chatYou',
                )}
            >
                {getSenderName(message)}
            </div>
            <div className="text-gray-500">
                <MessageItemSent timeString={time} />
            </div>
        </div>
    );
};

const PureMessageHeader = React.memo(MessageHeader);

interface Props {
    messageList: List<TypedMap<WidgetMessage>>;
    chatSessionKey: ChatSessionKey;
    extensionKey: ExtensionKey;
}

const PbxWidgetMessageItem = ({
    messageList,
    chatSessionKey,
    extensionKey,
}: Props) => {
    const firstMessage = messageList.first();

    return (
        <div className="flex flex-1 gap-3 py-3">
            <div>
                <ExtensionContactAvatar
                    contact={firstMessage.getIn(['from', 'contact'])}
                    size="medium"
                />
            </div>
            <div>
                <PureMessageHeader
                    message={firstMessage}
                    extensionKey={extensionKey}
                />
                <PureMessageWrapper
                    chatMessageList={messageList}
                    sessionKey={chatSessionKey}
                />
            </div>
        </div>
    );
};

export default React.memo(PbxWidgetMessageItem);
